import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { utils, writeFile } from 'xlsx';
import { format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';

const ReportGenerator = ({ reportData, detailedData, generateReport, startDate, endDate }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#82ca9d', '#ff6961'];

  const exportExcel = () => {
    const wb = utils.book_new();
    
    // Summary Sheet
    const summarySheet = utils.json_to_sheet(reportData.map(item => ({
      'Staff Name': item.name,
      'Role': item.role,
      'Present Days': item.presentDays,
      'Absent Days': item.totalDays - item.presentDays,
      'Total Days': item.totalDays,
      'Attendance %': `${((item.presentDays / item.totalDays) * 100 || 0).toFixed(2)}%`
    })));
    utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Detailed Sheet
    const detailedSheet = utils.json_to_sheet(detailedData?.map(item => ({
      'Date': format(parseISO(item.date), 'yyyy-MM-dd'),
      'Staff Name': item.staff.name,
      'Role': item.staff.role,
      'Status': item.isPresent ? 'Present' : 'Absent',
      'Marked At': format(parseISO(item.markedAt), 'yyyy-MM-dd HH:mm:ss')
    })) || []);
    utils.book_append_sheet(wb, detailedSheet, 'Details');

    writeFile(wb, `Attendance_Report_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}.xlsx`);
  };

  const renderTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-semibold">{data.name}</p>
          <p>Present: {data.presentDays}</p>
          <p>Absent: {data.totalDays - data.presentDays}</p>
          <p>Total: {data.totalDays} days</p>
          <p>Attendance: {((data.presentDays / data.totalDays) * 100 || 0).toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <div className="flex flex-col md:flex-row gap-4 items-end mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-2">Date Range</label>
          <div className="flex gap-2">
            <DatePicker
              selected={startDate}
              onChange={date => generateReport(date, endDate)}
              className="w-full p-2 border rounded"
              dateFormat="dd MMM yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={date => generateReport(startDate, date)}
              className="w-full p-2 border rounded"
              dateFormat="dd MMM yyyy"
              minDate={startDate}
            />
          </div>
        </div>
        <button
          onClick={() => generateReport(startDate, endDate)}
          className="bg-[#AB3430] text-white px-6 py-2 rounded hover:bg-[#8a2b28] h-[42px]"
        >
          Refresh Report
        </button>
      </div>

      {reportData?.length > 0 ? (
        <div className="space-y-8">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Summary Overview</h3>
            <div className="mb-4 text-lg">
              Total Days in Range: {reportData[0]?.totalDays || 0}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-4 rounded-md shadow">
                <h4 className="text-lg font-medium mb-4">Attendance Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData}
                      dataKey="presentDays"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {reportData.map((_, i) => (
                        <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={renderTooltip} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white p-4 rounded-md shadow">
                <h4 className="text-lg font-medium mb-4">Attendance Trends</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="presentDays" fill="#82ca9d" name="Present" />
                    <Bar dataKey={data => data.totalDays - data.presentDays} fill="#ff6961" name="Absent" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Detailed Records</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="p-3 text-left">Staff Member</th>
                    <th className="p-3 text-left">Role</th>
                    <th className="p-3 text-left">Present</th>
                    <th className="p-3 text-left">Absent</th>
                    <th className="p-3 text-left">Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((staff, i) => (
                    <tr key={i} className="border-b hover:bg-gray-100">
                      <td className="p-3">{staff.name}</td>
                      <td className="p-3">{staff.role}</td>
                      <td className="p-3 text-green-600">{staff.presentDays}</td>
                      <td className="p-3 text-red-600">{staff.totalDays - staff.presentDays}</td>
                      <td className="p-3 font-medium">
                        {((staff.presentDays / staff.totalDays) * 100 || 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={exportExcel}
              className="bg-[#AB3430] text-white px-6 py-2 rounded hover:bg-[#8a2b28]"
            >
              Export to Excel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {reportData ? 'No attendance records found' : 'Select date range to generate report'}
        </div>
      )}
    </div>
  );
};

export default ReportGenerator;